import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiError, http } from "@/lib/api";
import { useSeo } from "@/components/site/common";
import AdminProducts from "./AdminProducts";
import AdminCategories from "./AdminCategories";
import AdminOrders from "./AdminOrders";
import AdminSettings from "./AdminSettings";
import AdminAccount from "./AdminAccount";
import AdminReviews from "./AdminReviews";

function Login({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await http.post("/auth/login", { email, password }); onDone(data); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--lilac)] p-5">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-[2rem] bg-white p-8 shadow-xl" data-testid="admin-login-form">
        <img src="/logo.jpg" alt="DUSA" className="h-14 w-14 rounded-2xl" />
        <h1 className="font-display text-3xl font-bold">Painel DUSA</h1>
        <input data-testid="admin-login-email" type="email" required className="field" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="admin-login-password" type="password" required className="field" placeholder="Palavra-passe" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="admin-login-submit" disabled={busy} className="btn-primary w-full">{busy ? "A entrar…" : "Entrar"}</button>
      </form>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  useSeo("Painel");
  useEffect(() => { http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)); }, []);
  if (user === null) return <div className="p-10 text-center text-sm">A carregar…</div>;
  if (!user) return <Login onDone={setUser} />;
  const logout = async () => { await http.post("/auth/logout"); setUser(false); };
  const tab = "rounded-full px-5 py-2 data-[state=active]:bg-[var(--ink)] data-[state=active]:text-white";
  return (
    <div className="min-h-screen bg-[var(--paper)]" data-testid="admin-dashboard">
      <header className="border-b border-[var(--line)] bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-5 py-4">
          <img src="/logo.jpg" alt="" className="h-10 w-10 rounded-xl" />
          <p className="font-display text-xl font-bold">Painel DUSA</p>
          <Link to="/" target="_blank" className="ml-auto flex items-center gap-1 text-sm text-[var(--violet)]" data-testid="admin-view-site-link">Ver site <ExternalLink className="h-3.5 w-3.5" /></Link>
          <button data-testid="admin-logout-button" onClick={logout} className="btn-ghost !px-4 !py-2"><LogOut className="h-4 w-4" />Sair</button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-8">
        <Tabs defaultValue="orders">
          <TabsList className="h-auto flex-wrap gap-1 rounded-full bg-[var(--lilac)] p-1">
            <TabsTrigger value="orders" className={tab} data-testid="admin-tab-orders">Pedidos</TabsTrigger>
            <TabsTrigger value="products" className={tab} data-testid="admin-tab-products">Produtos</TabsTrigger>
            <TabsTrigger value="reviews" className={tab} data-testid="admin-tab-reviews">Avaliações</TabsTrigger>
            <TabsTrigger value="categories" className={tab} data-testid="admin-tab-categories">Categorias</TabsTrigger>
            <TabsTrigger value="settings" className={tab} data-testid="admin-tab-settings">Textos e definições</TabsTrigger>
            <TabsTrigger value="account" className={tab} data-testid="admin-tab-account">Conta</TabsTrigger>
          </TabsList>
          <TabsContent value="orders" className="mt-8"><AdminOrders /></TabsContent>
          <TabsContent value="products" className="mt-8"><AdminProducts /></TabsContent>
          <TabsContent value="reviews" className="mt-8"><AdminReviews /></TabsContent>
          <TabsContent value="categories" className="mt-8"><AdminCategories /></TabsContent>
          <TabsContent value="settings" className="mt-8"><AdminSettings /></TabsContent>
          <TabsContent value="account" className="mt-8"><AdminAccount user={user} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
